import { RangeModeTypes } from "PFApp/booking/types";
import { LoadingDots } from "PFComponents/loading_dots";
import SimplePagination from "PFComponents/pagination/simple_pagination";
import React, { useMemo, useRef } from "react";

import { useCalendarGridScope, useCalendarScrolling } from "../../hooks";
import Calendar, { FILTERS_ROW_HEIGHT } from "../calendar/calendar";
import { useBookingsOverviewContext } from "../context/bookings_overview_context";
import { useCalendarContext } from "../context/calendar_context";
import { SidePanelRows } from "../side_panel_rows";
import { ActionsBar } from "./actions_bar";
import css from "./bookings_view.module.scss";
import { ViewTabs } from "./view_tabs";

export type RangeMode = {
  type: RangeModeTypes;
  start?: string;
  end?: string;
};

export const BookingsView = (): JSX.Element => {
  const {
    calendarData: { data, page, setPage }
  } = useBookingsOverviewContext();

  const { calendarHeaderRef, calendarScrollWrapperRef, rangeMode, setRangeMode } = useCalendarContext();

  const { widthScrolled, scrollToDate, scrollToLeftOffset, handleCalendarViewScroll } = useCalendarScrolling({
    calendarHeaderRef,
    calendarScrollWrapperRef
  });

  const actionsBarRef = useRef<HTMLDivElement>(null);
  const calendarGridScope = useCalendarGridScope();

  const offsetTop = useMemo(() => {
    if (actionsBarRef.current) {
      const { y: yValue, height } = actionsBarRef.current.getBoundingClientRect();
      return yValue + height;
    } else {
      return 0;
    }
  }, [actionsBarRef.current]);

  const handlePageChange = (pageNumber: number) => setPage(pageNumber);

  return (
    <div className={css.container}>
      <div className={css.sidePanel}>
        <div className={css.sticky} style={{ top: FILTERS_ROW_HEIGHT }}>
          <ViewTabs />
          <ActionsBar ref={actionsBarRef} />
        </div>
        <SidePanelRows scrollToDate={scrollToDate} />
        <div className={css.footer}>
          <SimplePagination
            className={css.pagination}
            currentPage={page}
            handleClick={handlePageChange}
            totalPages={data.meta.totalPages}
            scrollOnClick={false}
            customSpan={1}
          />
        </div>
      </div>
      <Calendar
        headerRef={calendarHeaderRef}
        offsetTop={offsetTop}
        scrollWrapperRef={calendarScrollWrapperRef}
        startDate={calendarGridScope.min}
        endDate={calendarGridScope.max}
        rangeMode={rangeMode}
        onRangeModeChange={(value) => setRangeMode(value)}
        widthScrolled={widthScrolled}
        scrollToLeftOffset={scrollToLeftOffset}
        handleCalendarViewScroll={handleCalendarViewScroll}
      />
      {data.loading && <LoadingDots circlesEnabled className={css.loadingDots} />}
    </div>
  );
};
